import { Form } from '../components/ui/validated-form';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, setupAPI } from '../lib/api';
import { dispatchSetupTourStatusChanged, readSetupTourState, startSetupTour } from '../lib/setupTour';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteAccepted = searchParams.get('invite') === 'accepted';

  useEffect(() => {
    const presetEmail = (searchParams.get('email') || '').trim();
    if (presetEmail) setEmail(presetEmail);
    if (searchParams.get('recovery') === '1') { setForgotOpen(true); setForgotEmail(presetEmail); }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loggedInUser = await login(email, password);
      toast.success('Erfolgreich angemeldet');
      const next = searchParams.get('next');
      const normalizedRole = String(loggedInUser?.role || '').toLowerCase();
      if (!next && (normalizedRole === 'owner' || normalizedRole === 'admin')) {
        try {
          const setupRes = await setupAPI.getStatus();
          const setupStatus = setupRes.data || {};
          const existingTour = readSetupTourState();
          const sameOrgTour = existingTour?.orgId === setupStatus.org_id ? existingTour : null;
          if (!setupStatus.onboarding_dismissed && !setupStatus.completed_at && (inviteAccepted || !sameOrgTour?.currentStep)) {
            startSetupTour('profile', { orgId: setupStatus.org_id });
            dispatchSetupTourStatusChanged();
            navigate('/setup', { replace: true });
            return;
          }
        } catch (_err) {
          // fallback to dashboard below
        }
      }
      navigate(next || '/dashboard', { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>

          <div className="bg-white rounded-3xl p-8 shadow-sm">
            <div className="mb-8">
              <img
                src="/images/mib-logo-full.png"
                alt="MIB Logo"
                className="h-12 w-auto object-contain"
              />
            </div>

            <h1 className="text-3xl font-bold text-[#1D1D1F] mb-2">Willkommen zurück</h1>
            <p className="text-[#86868B] mb-8">Melde dich an, um fortzufahren</p>
            {inviteAccepted && (
              <div className="mb-6 rounded-2xl bg-[#F5F5F7] px-4 py-3 text-sm text-[#1D1D1F]">
                Passwort wurde gesetzt. Bitte melde dich mit deinem neuen Passwort an.
              </div>
            )}

            <Form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1D1D1F]">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base"
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[#1D1D1F]">Passwort</Label>
                  <button
                    type="button"
                    onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                    className="text-sm text-[#0071E3] hover:underline"
                  >
                    Passwort vergessen?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-base pr-10"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Wird angemeldet...
                  </span>
                ) : (
                  'Anmelden'
                )}
              </Button>
            </Form>

            <p className="mt-8 text-center text-[#86868B]">
              Noch kein Konto{' '}
              <Link to="/register" className="text-[#0071E3] hover:underline font-medium">
                Jetzt registrieren
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent sending={forgotLoading} sendingLabel="E-Mail" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Passwort vergessen</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#86868B] mb-4">
            Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen des Passworts.
          </p>
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              const em = (forgotEmail || '').trim();
              if (!em) {
                toast.error('Bitte E-Mail-Adresse eingeben');
                return;
              }
              setForgotLoading(true);
              try {
                await authAPI.forgotPassword({ email: em });
                toast.success('Falls ein Konto existiert, wurde ein Link gesendet. Prüfe dein Postfach.');
                setForgotOpen(false);
                setForgotEmail('');
              } catch (err) {
                toast.error(err?.response?.data?.detail || 'Fehler beim Senden');
              } finally {
                setForgotLoading(false);
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="forgot-email">E-Mail</Label>
              <Input
                id="forgot-email"
                type="email"
                autoComplete="email"
                placeholder="name@beispiel.de"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="input-base"
                required
              />
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={forgotLoading}>
              {forgotLoading ? 'Wird gesendet...' : 'Link senden'}
            </Button>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
          alt="Professional aesthetic physician administering treatment"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/45 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white text-2xl font-medium leading-relaxed">
            "Mit Make It Booking haben wir unseren gesamten Behandlungsablauf digitalisiert. Unsere Patienten lieben die Online-Buchung und wir sparen täglich wertvolle Zeit im Praxisalltag."
          </blockquote>
          <p className="mt-4 text-white/80 font-medium">— Dr. med. Sarah Brandner, Fachärztin für Plastische und Ästhetische Chirurgie</p>
        </div>
      </div>
    </div>
  );
}
